import { Fragment, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { IconButton } from "./Button";
import cn, { focusRing } from "./cn";

/**
 * RouteTabs — sections of one workspace that are real routes. Links, not tab
 * state: the URL, back button and deep links all work. One underline
 * treatment; counts only when they are worth reading.
 */
/**
 * On a narrow screen a row of tabs scrolls sideways; keep the selected one in view (only the tab row scrolls, never the
 * page). `selector` finds the selected tab inside the scrolling element; `key` re-runs it when the selection changes.
 */
export function useSelectedTabInView(selector, key) {
  const ref = useRef(null);
  useEffect(() => {
    // After layout (labels and counts can arrive a moment later and widen the row).
    const frame = requestAnimationFrame(() => {
      const row = ref.current;
      const active = row?.querySelector(selector);
      if (!row || !active || row.scrollWidth <= row.clientWidth) return;
      const rowBox = row.getBoundingClientRect();
      const box = active.getBoundingClientRect();
      if (box.left < rowBox.left) row.scrollLeft -= rowBox.left - box.left + 16;
      else if (box.right > rowBox.right) row.scrollLeft += box.right - rowBox.right + 16;
    });
    return () => cancelAnimationFrame(frame);
  }, [selector, key]);
  return ref;
}

export function RouteTabs({ items, label, className }) {
  const { pathname } = useLocation();
  const ref = useSelectedTabInView('[aria-current="page"]', `${pathname}|${items.map((item) => `${item.label}:${item.count || 0}`).join(",")}`);
  return (
    <nav ref={ref} aria-label={label} className={cn("-mb-px overflow-x-auto overflow-y-hidden border-b border-subtle", className)}>
      <ul className="flex min-w-max gap-6">
        {items.map((item) => (
          <li key={item.to}>
            <NavLink
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "relative inline-flex h-10 items-center gap-1.5 text-body-sm font-medium transition-colors max-md:h-touch",
                  "after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:rounded-full",
                  isActive ? "text-foreground after:bg-accent" : "text-secondary after:bg-transparent hover:text-foreground",
                  focusRing
                )
              }
            >
              {item.label}
              {item.count > 0 ? <span className="text-meta tabular-nums text-tertiary">{item.count}</span> : null}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}

/**
 * Breadcrumb — where the page sits: Module › Page. The last item is the
 * current page and is not a link.
 */
export function Breadcrumb({ items, className }) {
  if (!items?.length) return null;
  return (
    <nav aria-label="Breadcrumb" className={cn("min-w-0", className)}>
      <ol className="flex min-w-0 items-center gap-1.5 text-body-sm">
        {items.map((item, index) => {
          const last = index === items.length - 1;
          return (
            <Fragment key={`${item.label}-${index}`}>
              {/* On phones only the current page shows, so its separators go too. */}
              {index > 0 ? <li aria-hidden="true" className="shrink-0 text-tertiary max-sm:hidden">/</li> : null}
              <li className={cn("min-w-0", last ? "truncate" : "shrink-0 max-sm:hidden")}>
                {last || !item.to ? (
                  <span aria-current={last ? "page" : undefined} className={last ? "font-medium text-foreground" : "text-secondary"}>
                    {item.label}
                  </span>
                ) : (
                  <Link to={item.to} className={cn("rounded-sm text-secondary hover:text-foreground", focusRing)}>
                    {item.label}
                  </Link>
                )}
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}

function pageWindow(page, totalPages, span = 5) {
  let start = Math.max(1, page - Math.floor(span / 2));
  const end = Math.min(totalPages, start + span - 1);
  start = Math.max(1, end - span + 1);
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

/**
 * Pagination — previous / numbered pages / next, with the position stated in
 * words for screen readers and on small screens.
 */
export function Pagination({ page, totalPages, onPageChange, label = "Pagination", className }) {
  if (!totalPages || totalPages <= 1) return null;
  const pages = pageWindow(page, totalPages);
  const pageButton = (p) => (
    <button
      key={p}
      type="button"
      onClick={() => onPageChange(p)}
      aria-current={p === page ? "page" : undefined}
      aria-label={`Page ${p}`}
      className={cn(
        "inline-flex h-control-sm min-w-8 items-center justify-center rounded-control px-2 text-control-sm tabular-nums transition-colors",
        p === page ? "bg-accent-subtle text-accent" : "text-secondary hover:bg-hover hover:text-foreground",
        focusRing
      )}
    >
      {p}
    </button>
  );
  return (
    <nav aria-label={label} className={cn("flex items-center justify-between gap-3 py-3", className)}>
      <p className="text-meta tabular-nums text-secondary">
        Page {page} of {totalPages}
      </p>
      <div className="flex items-center gap-1">
        <IconButton icon={ChevronLeft} label="Previous page" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)} />
        <div className="flex items-center gap-1 max-sm:hidden">
          {pages[0] > 1 ? (
            <>
              {pageButton(1)}
              {pages[0] > 2 ? <span aria-hidden="true" className="px-1 text-tertiary">…</span> : null}
            </>
          ) : null}
          {pages.map(pageButton)}
          {pages[pages.length - 1] < totalPages ? (
            <>
              {pages[pages.length - 1] < totalPages - 1 ? <span aria-hidden="true" className="px-1 text-tertiary">…</span> : null}
              {pageButton(totalPages)}
            </>
          ) : null}
        </div>
        <IconButton icon={ChevronRight} label="Next page" size="sm" disabled={page >= totalPages} onClick={() => onPageChange(page + 1)} />
      </div>
    </nav>
  );
}

/**
 * Toolbar — search, one to three filters and the view's actions on one line
 * above a list. No card around it.
 */
export function Toolbar({ children, actions, className, label }) {
  return (
    <div aria-label={label} className={cn("flex flex-wrap items-center gap-2", className)}>
      {children}
      {actions ? <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}
